import React, { useEffect, useState } from 'react';
import { Card, Button, Select, Table, Input, Alert, Space, Typography, message, Tabs, Tag, Upload } from 'antd';
import { UploadOutlined, CopyOutlined, ReloadOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import apiService from 'services/api/apiService';

const { Text, Paragraph } = Typography;

const pad = (n: number) => String(n).padStart(2, '0');
const toTs = (v: any) => v instanceof Date
  ? `${v.getFullYear()}-${pad(v.getMonth() + 1)}-${pad(v.getDate())} ${pad(v.getHours())}:${pad(v.getMinutes())}:${pad(v.getSeconds())}`
  : String(v == null ? '' : v).trim();
const toDate = (v: any) => v instanceof Date
  ? `${v.getFullYear()}-${pad(v.getMonth() + 1)}-${pad(v.getDate())}`
  : String(v == null ? '' : v).trim();
const toTime = (v: any) => v instanceof Date
  ? `${pad(v.getHours())}:${pad(v.getMinutes())}:${pad(v.getSeconds())}`
  : String(v == null ? '' : v).trim();

const MATCH_OPTIONS = [
  { value: 'employee_id', label: 'Staff code (e.g. EMP001)' },
  { value: 'id', label: 'Internal HR ID (number)' },
  { value: 'biometric_id', label: 'Biometric / Badge ID' },
];

const BiometricIntegration: React.FC = () => {
  // ---- file import state ----
  const [sheet, setSheet] = useState<any[][]>([]);
  const [fileName, setFileName] = useState('');
  const [idxId, setIdxId] = useState<number | undefined>(undefined);
  const [idxDate, setIdxDate] = useState<number | undefined>(undefined);
  const [idxTime, setIdxTime] = useState<number | undefined>(undefined);
  const [matchBy, setMatchBy] = useState('employee_id');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<any>(null);

  // ---- push (door 2) state ----
  const [pushKey, setPushKey] = useState('');
  const [pushEndpoint, setPushEndpoint] = useState('');
  const [pushMatch, setPushMatch] = useState('biometric_id');
  const [keyBusy, setKeyBusy] = useState(false);

  // ---- device id map state ----
  const [mapRows, setMapRows] = useState<any[]>([]);
  const [mapLoading, setMapLoading] = useState(false);

  const header: any[] = sheet.length ? sheet[0] : [];
  const colOptions = header.map((h, i) => ({ value: i, label: String(h === '' || h == null ? `Column ${i + 1}` : h) }));

  const loadKey = async () => {
    const r = await apiService.getDeviceKey();
    if (r && r.success) {
      setPushKey(r.push_key || '');
      setPushMatch(r.match_by || 'biometric_id');
      setPushEndpoint((typeof window !== 'undefined' ? window.location.origin : '') + (r.endpoint || ''));
    }
  };
  const loadMap = async () => {
    setMapLoading(true);
    const r = await apiService.getBiometricMap();
    if (r && r.success) setMapRows(r.data || []);
    setMapLoading(false);
  };
  useEffect(() => { loadKey(); loadMap(); }, []);

  const handleFile = (file: File) => {
    setResult(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target!.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: 'array', cellDates: true });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: '' });
        const nonEmpty = rows.filter(r => Array.isArray(r) && r.some(c => c !== '' && c !== null && c !== undefined));
        setSheet(nonEmpty);
        setFileName(file.name);
        setIdxId(undefined); setIdxDate(undefined); setIdxTime(undefined);
        if (!nonEmpty.length) message.warning('That file appears to be empty.');
      } catch (err) {
        message.error('Could not read that file. Export it as CSV or XLSX and try again.');
      }
    };
    reader.readAsArrayBuffer(file);
    return false; // stop antd from uploading
  };

  const buildTimestamp = (row: any[]) => {
    const d = row[idxDate as number];
    if (idxTime === undefined || idxTime === null) return toTs(d);
    return `${toDate(d)} ${toTime(row[idxTime])}`.trim();
  };

  const previewRows = sheet.slice(1, 7).map((r, i) => ({
    key: i,
    device_id: String(r[idxId as number] == null ? '' : r[idxId as number]).trim(),
    timestamp: (idxDate !== undefined) ? buildTimestamp(r) : '',
  }));

  const doImport = async () => {
    if (idxId === undefined || idxDate === undefined) { message.warning('Pick the ID column and the date/time column.'); return; }
    const punches = sheet.slice(1)
      .map(r => ({ device_id: String(r[idxId as number] == null ? '' : r[idxId as number]).trim(), timestamp: buildTimestamp(r) }))
      .filter(p => p.device_id !== '');
    if (!punches.length) { message.warning('No usable rows found with that column mapping.'); return; }
    setImporting(true);
    const r = await apiService.importDevicePunches(punches, matchBy, 'biometric');
    setImporting(false);
    setResult(r);
    if (r && r.success) message.success(`Imported ${r.imported} new, updated ${r.updated} record(s).`);
    else message.error((r && r.error) || 'Import failed');
  };

  const regenerate = async () => {
    setKeyBusy(true);
    const r = await apiService.regenerateDeviceKey(pushMatch);
    setKeyBusy(false);
    if (r && r.success) { setPushKey(r.push_key); message.success('New device key generated.'); }
    else message.error('Could not generate key');
  };
  const savePushMatch = async (v: string) => {
    setPushMatch(v);
    const r = await apiService.setDevicePushMatch(v);
    if (r && r.success) message.success('Push match setting saved.');
  };
  const copy = (txt: string) => {
    try { navigator.clipboard.writeText(txt); message.success('Copied'); } catch { message.info('Copy manually'); }
  };

  const saveBiometricId = async (empId: number, val: string) => {
    const r = await apiService.setEmployeeBiometricId(empId, val);
    if (r && r.success) { message.success('Saved'); loadMap(); }
    else message.error('Could not save');
  };

  const sampleCurl = `curl -X POST "${pushEndpoint}" \\\n  -H "Content-Type: application/json" \\\n  -d '{"device_key":"${pushKey || '<KEY>'}","punches":[{"device_id":"1001","timestamp":"2026-10-18 08:03:00"}]}'`;

  const importTab = (
    <div>
      <Alert type="info" showIcon style={{ marginBottom: 12 }}
        message="Works with any device"
        description="Export the punch log from your device (ZKTeco, Virdi, Hikvision, etc.) as CSV or Excel, upload it, then tell us which columns hold the ID and the date/time. Re-uploading the same period updates records rather than duplicating them." />
      <Space wrap style={{ marginBottom: 12 }}>
        <Upload beforeUpload={handleFile as any} showUploadList={false} accept=".csv,.xlsx,.xls,.txt">
          <Button icon={<UploadOutlined />}>Choose device export file</Button>
        </Upload>
        {fileName && <Tag color="blue">{fileName} · {Math.max(0, sheet.length - 1)} rows</Tag>}
      </Space>
      {sheet.length > 0 && (
        <Space wrap style={{ marginBottom: 12 }}>
          <span>ID column:
            <Select style={{ width: 200, marginLeft: 6 }} value={idxId} placeholder="Select column"
              options={colOptions} onChange={setIdxId} /></span>
          <span>Date/Time column:
            <Select style={{ width: 200, marginLeft: 6 }} value={idxDate} placeholder="Select column"
              options={colOptions} onChange={setIdxDate} /></span>
          <span>Separate time column (optional):
            <Select style={{ width: 200, marginLeft: 6 }} value={idxTime} placeholder="None" allowClear
              options={colOptions} onChange={(v) => setIdxTime(v)} /></span>
          <span>Match device ID to:
            <Select style={{ width: 230, marginLeft: 6 }} value={matchBy}
              options={MATCH_OPTIONS} onChange={setMatchBy} /></span>
        </Space>
      )}
      {sheet.length > 0 && idxId !== undefined && idxDate !== undefined && (
        <Card size="small" title="Preview (first rows)" style={{ marginBottom: 12 }}>
          <Table size="small" pagination={false} dataSource={previewRows}
            columns={[{ title: 'Device ID', dataIndex: 'device_id' }, { title: 'Timestamp', dataIndex: 'timestamp' }]} />
        </Card>
      )}
      <Button type="primary" loading={importing} disabled={!sheet.length} onClick={doImport}>Import attendance</Button>
      {result && result.success && (
        <Alert style={{ marginTop: 12 }} type="success" showIcon
          message={`Imported ${result.imported} new, updated ${result.updated}. Matched ${result.employees_matched} employee(s) across ${result.days} day(s).`}
          description={
            <span>
              {result.unmatched && result.unmatched.length > 0 && <div>Unmatched device IDs (no employee): {result.unmatched.join(', ')}</div>}
              {result.unparsed_timestamps > 0 && <div>{result.unparsed_timestamps} row(s) had an unreadable date/time and were skipped.</div>}
              <div>Batch reference: {result.import_reference}</div>
            </span>
          } />
      )}
      {result && !result.success && <Alert style={{ marginTop: 12 }} type="error" showIcon message={result.error || 'Import failed'} />}
    </div>
  );

  const pushTab = (
    <div>
      <Alert type="info" showIcon style={{ marginBottom: 12 }}
        message="Live push endpoint (for network devices / middleware)"
        description="Network-enabled devices or a small bridge script can POST punches straight to this URL using the key below. No file upload needed. Same engine as the file import." />
      <Paragraph><Text strong>Endpoint</Text><br />
        <Input readOnly value={pushEndpoint} addonAfter={<CopyOutlined onClick={() => copy(pushEndpoint)} />} /></Paragraph>
      <Paragraph><Text strong>Device API key</Text><br />
        <Space.Compact style={{ width: '100%' }}>
          <Input.Password readOnly value={pushKey} />
          <Button icon={<CopyOutlined />} onClick={() => copy(pushKey)}>Copy</Button>
          <Button icon={<ReloadOutlined />} loading={keyBusy} onClick={regenerate}>Regenerate</Button>
        </Space.Compact>
      </Paragraph>
      <Paragraph><Text strong>Match pushed IDs to</Text>{'  '}
        <Select style={{ width: 230 }} value={pushMatch} options={MATCH_OPTIONS} onChange={savePushMatch} />
      </Paragraph>
      <Paragraph><Text strong>Sample request</Text>
        <pre style={{ background: '#f5f5f5', padding: 12, borderRadius: 6, whiteSpace: 'pre-wrap', fontSize: 12 }}>{sampleCurl}</pre>
      </Paragraph>
      <Alert type="warning" showIcon message="Keep this key secret — anyone with it can post attendance for your organisation. Regenerate it if it leaks." />
    </div>
  );

  const mapColumns = [
    { title: 'Name', key: 'name', render: (_: any, r: any) => `${r.first_name || ''} ${r.last_name || ''}`.trim() },
    { title: 'Staff code', dataIndex: 'employee_id' },
    { title: 'Dept', dataIndex: 'department' },
    {
      title: 'Biometric / Badge ID', key: 'bio',
      render: (_: any, r: any) => (
        <Input defaultValue={r.biometric_id || ''} style={{ width: 160 }} placeholder="device user id"
          onPressEnter={(e) => saveBiometricId(r.id, (e.target as HTMLInputElement).value)}
          onBlur={(e) => { if ((e.target.value || '') !== (r.biometric_id || '')) saveBiometricId(r.id, e.target.value); }} />
      ),
    },
  ];

  const mapTab = (
    <div>
      <Alert type="info" showIcon style={{ marginBottom: 12 }}
        message="Device ID mapping"
        description="Only needed if you match on Biometric / Badge ID. Enter the ID each person is enrolled under on the device; press Enter or click away to save. If your device uses the staff code or internal HR ID, you can skip this." />
      <Table size="small" rowKey="id" loading={mapLoading} dataSource={mapRows} columns={mapColumns as any} pagination={{ pageSize: 10 }} />
    </div>
  );

  return (
    <Card style={{ marginBottom: 16, borderColor: '#fa8c16' }} title="👆 Attendance import & device sync">
      <Tabs
        items={[
          { key: 'import', label: 'Import file', children: importTab },
          { key: 'push', label: 'Live push (API)', children: pushTab },
          { key: 'map', label: 'Device ID map', children: mapTab },
        ]}
      />
    </Card>
  );
};

export default BiometricIntegration;
